import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { Status } from "Util/enums";

import { Button } from "../../../../../../components/Form";
import { Color } from "../../../../../../components/Form/Button";
import IconAdd from "../../../../../../components/Generic/Icons/Add";
import { PRIVATE_ROUTES } from "../../../../../../constants";
import { Filters, Searches, Sort, Sorts } from "../../../../../../models/shipeezi.table";
import { Subcontractor } from "../../../../../../models/subcontractor";
import { useSubcontractorActions } from "./hooks/useActions";
import useStyles from "./styles";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";

const renderValueOrHifen = (value?: any) => value || "-";

interface SubcontractorGridColumn extends Omit<Column, "name"> {
  name: keyof Subcontractor;
}

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
    changeStatus: boolean;
  };
};

const Subcontractors = (props: Props) => {
  const { permission } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const history = useHistory();

  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const { getAllSubcontractors, countSubcontractors, useUpdateSubcontractor } =
    useSubcontractorActions();

  const {
    isFetching,
    data: subcontractorsData,
    refetch: refetchGetAll,
  } = getAllSubcontractors(page, pageSize, searches, filters, sorting);
  const { data: subcontractorsCountData } = countSubcontractors(filters, searches);

  const { mutate: updateSubcontractor, isLoading: isUpdating } =
    useUpdateSubcontractor(refetchGetAll);

  useEffect(() => {
    if (subcontractorsData) {
      setSubcontractors(subcontractorsData);
    }
  }, [subcontractorsData]);

  useEffect(() => {
    if (subcontractorsCountData) {
      setTotal(subcontractorsCountData);
    }
  }, [subcontractorsCountData]);

  const manageSubcontractor = (id: number, isView?: boolean) => {
    const state = history.location.state as any || {};
    history.push({
      pathname: `${PRIVATE_ROUTES.SUBCONTRACTORS_MNT}/${id}`,
      state: isView ? { ...state, viewOnly: isView } : state,
    });
  };

  const viewSubcontractor = (_id: number) => {
    const data = subcontractors.find((subcontractor) => Number(subcontractor.id) === Number(_id));

    return (
      <Tooltip title="View" placement="top">
        <div>
          <FiEye
            size={18}
            color={colors.gray600}
            className={classes.button}
            onClick={() => {
              if (data) {
                manageSubcontractor(data.id, true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const viewChangeStatus = (index: number) => {
    const data = subcontractors.find((subcontractor) => Number(subcontractor.id) === Number(index));

    return (
      <Tooltip title="Change Status" placement="top">
        <div className={classes.pointer}>
          <GridIcons.status
            color={colors.gray600}
            onClick={() => {
              if (data) {
                updateSubcontractor({
                  id: Number(data.id),
                  data: {
                    id: data.id,
                    companyId: data.companyId,
                    businessName: data.businessName,
                    businessEmail: data.businessEmail,
                    isActive: !data.isActive,
                  },
                });
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const columns: SubcontractorGridColumn[] = [
    { name: "businessName", title: translate("businessName"), searchIdentifier: "business_name" },
    {
      name: "businessLocation",
      title: translate("businessAddress"),
      hideSort: true,
      hideSearch: true,
      formatText: (value: Subcontractor["businessLocation"]) =>
        renderValueOrHifen(value?.addressLine1 ?? value?.longAddress),
    },
    {
      name: "fullName",
      title: translate("fullName"),
      searchIdentifier: "full_name",
      formatComponent: (value: Subcontractor["fullName"], row: Subcontractor) => {
        if (value) return value;

        return renderValueOrHifen(`${row?.firstName ?? ""} ${row?.lastName ?? ""}`.trim());
      },
    },
    {
      name: "businessPhone",
      title: translate("phone"),
      searchIdentifier: "business_phone",
      formatText: renderValueOrHifen,
    },
    {
      name: "businessEmail",
      title: translate("email"),
      searchIdentifier: "business_email",
      formatText: renderValueOrHifen,
    },
    {
      name: "members",
      title: translate("qtyMembers"),
      hideSort: true,
      hideSearch: true,
      formatText: (value: Subcontractor["members"]) => (value || []).length,
    },
    {
      name: "fleets",
      title: translate("qtyEquipments"),
      hideSort: true,
      hideSearch: true,
      formatText: (value: Subcontractor["fleets"]) => (value || []).length,
    },
    {
      name: "isActive",
      title: "Status",
      hideSearch: true,
      formatComponent: (status: boolean) => (
        <BadgeStatus value={status ? Status.ACTIVE : Status.INACTIVE} className={classes.status} />
      ),
    },
  ];

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newSubcontractor"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "isActive",
          options: [
            {
              value: "1",
              label: translate("active"),
            },
            {
              value: "0",
              label: translate("inactive"),
            },
          ],
        },
      ],
      onChange: handleFilter,
    },
    newSubcontractor:
      permission && isShowAction(currentUser, permission.create) ? (
        <Button
          customColor={Color.SECONDARY_DARK}
          data-cy="new-subcontractor-btn"
          onClick={() => {
            history.push(PRIVATE_ROUTES.SUBCONTRACTORS_MNT, history.location.state || {});
          }}
          startIcon={<IconAdd color={colors.white} height={20} width={20} />}
        >
          {translate("newSubcontractor")}
        </Button>
      ) : undefined,
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [
      {
        title: "View",
        key: "handleView",
        buttonAndAction: viewSubcontractor,
      },
    ];

    if (permission && isShowAction(currentUser, permission.changeStatus)) {
      actions?.push({
        title: "Change Status",
        key: "handleChangeStatus",
        buttonAndAction: viewChangeStatus,
      });
    }

    return actions;
  };

  return (
    <div data-cy="subcontractorsListTable" className={classes.main}>
      <ShipeeziNewGrid
        data={subcontractors}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChangeStatus"],
          handleEdit:
            permission && isShowAction(currentUser, permission.edit)
              ? (id) => manageSubcontractor(Number(id))
              : undefined,
        }}
        rowActionsExtra={handleRowActionsExtraByPermission()}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
    </div>
  );
};

export default Subcontractors;
